import { useAuthContext, useTimeZonedFunctions } from '/src/hooks';
import { abbreviatedIntervalType } from '/src/utils/string';
import { SUBSCRIPTION_STATES, PLAN_PLATFORMS, PLAN_TYPES, INTERVAL_TYPES } from '/src/features/_global/constants';
import { AppLink } from '/src/features/_global/components/AppLink';

const { CANCELING, CANCELED, EXPIRING, EXPIRED } = SUBSCRIPTION_STATES;

function IntervalInfo({ isMonthly }) {
  return <p>{isMonthly ? 'Monthly' : 'Annual'} subscription</p>;
}

function IsMonthlyOrYearly(name, intervalType) {
  if (intervalType === INTERVAL_TYPES.MONTHS && !name.match(/monthly/i)) {
    return '(Monthly)';
  }
  if (intervalType === INTERVAL_TYPES.YEARS && !name.match(/yearly/i)) {
    return '(Yearly)';
  }
  return '';
}

function ActiveDetail({ currentPlanTitle }) {
  const { subscription, currentUser } = useAuthContext();
  const { formatDay } = useTimeZonedFunctions();
  const { renewOn, intervalType, nextPaymentPlan, nextAmount, fallbackAfter, fallbackAmount, nextIntervalType } =
    currentUser.subscriptions.snapshots;

  const isShopifyUser = subscription.plan.platform === PLAN_PLATFORMS.SHOPIFY;
  const isTrial = subscription.plan.type === PLAN_TYPES.TRIAL;
  const isMonthly = intervalType === INTERVAL_TYPES.MONTHS;

  const nextAmountPrice = fallbackAmount ?? nextAmount;
  const nextRenewal = fallbackAfter ?? renewOn;

  return (
    <>
      <div className="text-body-4">{!isTrial && <IntervalInfo isMonthly={isMonthly} />}</div>
      <div className="text-body-4">
        {!isShopifyUser && nextPaymentPlan !== null && (
          <>
            Your plan will change from{' '}
            <strong>
              {currentPlanTitle} {IsMonthlyOrYearly(currentPlanTitle, intervalType)}
            </strong>{' '}
            to{' '}
            <strong>
              {nextPaymentPlan.name} {IsMonthlyOrYearly(nextPaymentPlan.name, nextIntervalType)}
            </strong>{' '}
            for ${nextAmountPrice.toLocaleString()}/{abbreviatedIntervalType(nextIntervalType)} on{' '}
            {nextRenewal ? formatDay(nextRenewal) : 'N/A'}.
          </>
        )}
        {[PLAN_TYPES.FREE].includes(subscription.plan.type) && !nextPaymentPlan && (
          <p>Upgrade your plan to access more Crazy Egg features! </p>
        )}
      </div>
    </>
  );
}

function InactiveDetail() {
  const { currentUser, subscription } = useAuthContext();
  const { formatDay } = useTimeZonedFunctions();

  const {
    paymentPlan: { id },
  } = currentUser.subscriptions.snapshots;

  const isInExpiredGrace = subscription.state === EXPIRING && ![181, 197].includes(id);

  if (isInExpiredGrace) {
    return (
      <p className="text-error">
        Your Account will be deactivated soon due to a problem with payment.
        <br />
        <AppLink to="/account/billing?billingModal=true" className="text-link">
          Please update your payment info
        </AppLink>
        .
      </p>
    );
  }

  if ([CANCELING, CANCELED].includes(subscription.state)) {
    return (
      <p className="text-error">
        Subscription canceled.{' '}
        {subscription.state === CANCELING
          ? `Your subscription will not auto-renew once it expires on ${formatDay(subscription.endDate)}.`
          : ''}
      </p>
    );
  }

  if ([EXPIRING, EXPIRED].includes(subscription.state)) {
    return (
      <p className="text-error">
        Your subscription {subscription.state === EXPIRED ? `expired` : `expires`} on {formatDay(subscription.endDate)}.
      </p>
    );
  }
}

export function PlanSubscriptionInfo({ currentPlanTitle }) {
  const { subscription } = useAuthContext();

  return (
    <>
      {[CANCELING, CANCELED, EXPIRING, EXPIRED].includes(subscription.state) ? (
        <InactiveDetail />
      ) : (
        <ActiveDetail currentPlanTitle={currentPlanTitle} />
      )}
    </>
  );
}
